import {Component, DestroyRef, inject, Signal, viewChild} from '@angular/core';
import {FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {RecaptchaComponent, RecaptchaModule} from 'ng-recaptcha-2';
import {environment} from '../../../../../environments/environment.development';
import {TranslatePipe} from '@ngx-translate/core';
import {IContact, IContactForm} from '../../interfaces/contact.interface';
import {ContactService} from '../../services/contact/contact-service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Dialog} from '@angular/cdk/dialog';
import {ContactSuccess} from '../contact-success/contact-success';
import {ContactError} from '../contact-error/contact-error';
import {LoaderCircle, LucideAngularModule} from 'lucide-angular';

@Component({
  selector: 'app-contact-form',
  imports: [
    ReactiveFormsModule,
    RecaptchaModule,
    TranslatePipe,
    LucideAngularModule
  ],
  templateUrl: './contact-form.html',
  styles: ``
})
export class ContactForm {
  #contactService: ContactService = inject(ContactService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #dialog: Dialog = inject(Dialog);

  loading: Signal<boolean> = this.#contactService.loading;
  recaptcha = viewChild<RecaptchaComponent>('recaptcha');
  recaptchaSiteKey: string = environment.recaptchaSiteKey;

  contactForm: FormGroup<IContactForm> = inject(NonNullableFormBuilder).group<IContactForm>({
    email: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.email]}),
    fullname: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)]
    }),
    phone: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^(\+\d{1,3}[- ]?)?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/)]
    }),
    object: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(100)]
    }),
    message: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]
    }),
    recaptcha: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
  });

  get f(): IContactForm {
    return this.contactForm.controls;
  }

  onRecaptchaResolved(recaptchaResponse: string | null): void {
    if (recaptchaResponse) {
      this.contactForm.patchValue({
        recaptcha: recaptchaResponse
      });
    }
  }

  onSubmitForm(): void {
    this.contactForm.markAllAsTouched();
    if (this.contactForm.valid) {
      const payload: IContact = this.contactForm.getRawValue();
      this.#contactService.contact(payload)
        .pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
        next: () => {
          this.#dialog.open(ContactSuccess, {disableClose: true});
          this.contactForm.reset();
        },
        error: () => {
          this.#dialog.open(ContactError, {disableClose: true});
          this.recaptcha()?.reset();
        },
      });
    }
  }

  protected readonly LoaderCircle = LoaderCircle;
}
